import { useEffect, useMemo, useState, type RefObject } from 'react';

type SurfaceWidthModeOptions = {
  stackedBreakpointPx?: number;
  compactBreakpointPx?: number;
  measureRelativeToParent?: boolean;
};

type SurfaceWidthMode = {
  width: number;
  isStacked: boolean;
  isCompact: boolean;
  mode: 'full' | 'stacked' | 'compact';
};

const DEFAULT_STACKED_BREAKPOINT_PX = 1024;
const DEFAULT_COMPACT_BREAKPOINT_PX = 760;

export const useSurfaceWidthMode = (
  rootRef: RefObject<HTMLElement | null>,
  options?: SurfaceWidthModeOptions
): SurfaceWidthMode => {
  const stackedBreakpointPx = options?.stackedBreakpointPx ?? DEFAULT_STACKED_BREAKPOINT_PX;
  const compactBreakpointPx = options?.compactBreakpointPx ?? DEFAULT_COMPACT_BREAKPOINT_PX;
  const measureRelativeToParent = options?.measureRelativeToParent ?? false;
  const [width, setWidth] = useState(0);

  useEffect(() => {
    const root = rootRef.current;
    if (!root) {
      return () => {};
    }

    const syncWidth = () => {
      const rootWidth = root.getBoundingClientRect().width;
      const parentWidth = measureRelativeToParent && root.parentElement
        ? root.parentElement.getBoundingClientRect().width
        : rootWidth;
      const measuredWidth = measureRelativeToParent
        ? Math.min(rootWidth, parentWidth)
        : rootWidth;
      setWidth(Math.round(measuredWidth));
    };

    syncWidth();

    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', syncWidth);
      return () => window.removeEventListener('resize', syncWidth);
    }

    const observer = new ResizeObserver(syncWidth);
    observer.observe(root);
    return () => observer.disconnect();
  }, [measureRelativeToParent, rootRef]);

  return useMemo(() => {
    const isCompact = width > 0 && width <= compactBreakpointPx;
    const isStacked = width > 0 && width < stackedBreakpointPx;
    const mode: SurfaceWidthMode['mode'] = isCompact ? 'compact' : isStacked ? 'stacked' : 'full';
    return {
      width,
      isStacked,
      isCompact,
      mode,
    };
  }, [compactBreakpointPx, stackedBreakpointPx, width]);
};

export type { SurfaceWidthMode, SurfaceWidthModeOptions };
